import { clearGuestSession, getGuestToken } from '@pulse/utils/guest-session';

import { setGuestReturnPath } from '~/support/guest-return-path';

export const CLAIM_LINK_QUERY_PARAM = 't';

/**
 * Redeems the token from a claim email. Only reached for arrivals from an email, so the service is
 * imported here rather than at module scope — the middleware is evaluated on every navigation and
 * has no reason to drag the HTTP client along.
 */
const redeemClaimLink = async linkToken => {
  try {
    const { default: ClaimService } = await import('@pulse/services/ClaimService');

    await new ClaimService(useRuntimeConfig().public.apiBaseUrl).exchangeClaimLink(linkToken);
  } catch (error) {
    // Never rethrown: expired or tampered-with links are ordinary, lookup still gets the customer
    // where they were going, and whether redemption worked is decided by looking for the session it
    // should have left. Logged because a rejected token and a network or module-load failure are
    // otherwise indistinguishable in production, and only one of those is a bug.
    console.error(`Claim link redemption failed: ${error?.message ?? error}`);
  }
};

/** Both paths end the same way: proceed if a session is in hand, otherwise go and earn one. */
const allowOrSendToLookup = fullPath => {
  if (getGuestToken()) return;

  setGuestReturnPath(fullPath);

  return navigateTo('/start');
};

/**
 * Store template overrides written before the per-rating urls existed append their own `?rating=N` to
 * a link that already carries the token. Only the first `?` starts a query string, so the browser
 * folds the rest into the token's value and the signature no longer verifies.
 *
 * `?` cannot occur in a JWT — base64url is `A-Z a-z 0-9 - _` with `.` separators — so everything from
 * the first one is recoverable junk, and the rating inside it is the star the customer actually
 * clicked. Splitting here rescues links that have already been sent, including the ninety-day ones
 * sitting in inboxes, which no change to a stored template can reach.
 */
const readClaimLink = to => {
  const raw = to.query[CLAIM_LINK_QUERY_PARAM];
  if (typeof raw !== 'string' || !raw) return { strays: {}, token: '' };

  const [token, ...rest] = raw.split('?');

  return { strays: Object.fromEntries(new URLSearchParams(rest.join('?'))), token };
};

/**
 * The path without the link token, used both for the resume target and to tidy the address bar. A
 * retained `t` would be re-redeemed on the next navigation, and it is spent by then. Anything
 * rescued from a corrupted token goes back on, so the review page still sees the rating.
 */
const withoutLinkToken = (to, strays = {}) => {
  const query = { ...to.query, ...strays };
  delete query[CLAIM_LINK_QUERY_PARAM];

  const params = new URLSearchParams(
    Object.entries(query).flatMap(([key, value]) =>
      (Array.isArray(value) ? value : [value]).filter(v => v != null && v !== '').map(v => [key, String(v)]),
    ),
  );
  const search = params.toString();

  return search ? `${to.path}?${search}` : to.path;
};

/**
 * Applied to the pages reached by emailed deep links. They render claim and order data, so arriving
 * without a usable guest session — a link opened in a fresh tab, a bookmark, a typed URL — has to go
 * through lookup first.
 *
 * The redirect happens here rather than inside the page so no claim request is ever issued: a page
 * that fetched first and redirected on failure would still have asked the question.
 *
 * Deliberately not an `async` function. Every navigation to these pages runs this, and the ordinary
 * case — a session already in hand — is a synchronous check; making the whole middleware async would
 * push each of those through Nuxt's async-middleware handling for no reason.
 */
export default defineNuxtRouteMiddleware(to => {
  const { token: linkToken, strays } = readClaimLink(to);
  const resumePath = withoutLinkToken(to, strays);

  // Redeemed even when a session already exists, because that session may belong to a different
  // order — someone who looked up another order in between would otherwise reach this page with a
  // valid-looking token and be refused by the backend. The link is the more specific proof.
  if (linkToken) {
    // Dropped before redemption, not after. Holding on to it means a link that fails to redeem —
    // expired, tampered with, pointing at a deleted claim — falls back to a session for some other
    // order, and the page loads only to have its first request refused. Clearing first turns that
    // into a trip through lookup, which is where the customer needed to go anyway.
    clearGuestSession();

    return redeemClaimLink(linkToken).then(() => {
      const redirect = allowOrSendToLookup(resumePath);
      if (redirect) return redirect;

      // Redeemed. Drop the spent token from the URL so a refresh or a shared link does not try again.
      return navigateTo(resumePath, { replace: true });
    });
  }

  return allowOrSendToLookup(resumePath);
});
